import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import SEOHead from '../components/SEOHead';
import { PAGE_SEO } from '../lib/seo';
import { Logo } from '../components/Logo';
import { organizationSchema, softwareApplicationSchema, websiteSchema, howToSchema } from '../lib/structured-data';

export default function LandingPage() {
  return (
    <PublicLayout>
      <SEOHead
        title={PAGE_SEO['/'].title}
        description={PAGE_SEO['/'].description}
        canonical="/"
        skipSuffix
        jsonLd={[
          organizationSchema(),
          softwareApplicationSchema(),
          websiteSchema(),
          howToSchema([
            { name: 'Connect Stripe', text: 'One click OAuth. Read-only access. We never touch your money.' },
            { name: 'See Your SaaS Health', text: 'Health score, valuation range, and insights — calculated instantly.' },
            { name: 'Share Your Traction', text: 'Create a public profile. Get listed. Build credibility.' },
          ]),
        ]}
      />

      {/* Hero Section */}
      <section className="pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left - Text */}
            <div className="lg:w-1/2 text-center lg:text-left">
              <h1 className="text-5xl sm:text-6xl font-bold leading-tight">
                <span className="text-[#0043aa]">SaaS Health Score &amp; Metrics</span>{' '}
                <span className="text-[#11a566]">in 60 seconds.</span>
              </h1>
              <p className="mt-6 text-xl text-gray-600">
                Connect Stripe. Get your health score, real metrics, and valuation estimate instantly.
                <span className="block mt-2 text-gray-500">No spreadsheets. No guessing.</span>
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link to="/signup" className="btn-primary text-sm px-6 py-2.5">
                  Get Started Free
                </Link>
                <Link
                  to="/directory"
                  className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  View Public Profiles
                </Link>
              </div>
            </div>

            {/* Right - Product Screenshot */}
            <div className="lg:w-1/2">
              <div className="rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
                {/* Browser chrome */}
                <div className="bg-gray-900 px-3 py-2 flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  </div>
                  <div className="flex-1 text-center">
                    <span className="text-[10px] text-gray-400">plevits.com/dashboard</span>
                  </div>
                </div>
                {/* Dashboard preview */}
                <div className="p-3 sm:p-4 bg-gradient-to-br from-gray-50 to-white">
                  {/* Top metrics row */}
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100">
                      <div className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wider">ARR</div>
                      <div className="text-base font-bold text-gray-900">$1.2M</div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-[10px] text-green-600 font-medium">18%</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100">
                      <div className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wider">MRR</div>
                      <div className="text-base font-bold text-gray-900">$98.5K</div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-[10px] text-green-600 font-medium">12%</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100">
                      <div className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wider">NRR</div>
                      <div className="text-base font-bold text-gray-900">108%</div>
                      <div className="mt-0.5">
                        <span className="text-[10px] text-gray-400">Healthy</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100">
                      <div className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wider">Health</div>
                      <div className="text-base font-bold text-primary-600">
                        78<span className="text-[10px] text-gray-400">/100</span>
                      </div>
                      <div className="mt-0.5">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-green-100 text-green-700">
                          Strong
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Middle section - Outlook + Insights */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-lg p-3 border border-primary-100">
                      <div className="text-[10px] font-semibold text-primary-600 uppercase tracking-wider mb-1">
                        Business Outlook
                      </div>
                      <div className="text-xs font-bold text-gray-900 mb-1.5">
                        Strong growth trajectory
                      </div>
                      <p className="text-[10px] text-gray-600 mb-2">
                        Growing faster than 72% at your stage.
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]">
                        <div className="flex items-center gap-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          <span className="text-gray-600">Low churn</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          <span className="text-gray-600">Strong NRR</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                          <span className="text-gray-600">Watch concentration</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                      <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        What Changed This Month
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-[11px] font-medium text-gray-900">Churn dropped to 1.8%</div>
                            <div className="text-[10px] text-gray-500">Down from 2.4%</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-[11px] font-medium text-gray-900">+24 new customers</div>
                            <div className="text-[10px] text-gray-500">Best month this quarter</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-[11px] font-medium text-gray-900">ARPU up 8%</div>
                            <div className="text-[10px] text-gray-500">Expansion working</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom metrics row */}
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100">
                      <div className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wider">Valuation</div>
                      <div className="text-xs font-bold text-gray-900">$4.8M–$7.2M</div>
                      <div className="text-[10px] text-gray-400 mt-0.5">4-6x ARR</div>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100">
                      <div className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wider">Churn</div>
                      <div className="text-xs font-bold text-gray-900">1.8%</div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-[10px] text-green-600">Below median</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100">
                      <div className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wider">Customers</div>
                      <div className="text-xs font-bold text-gray-900">847</div>
                      <div className="text-[10px] text-gray-400 mt-0.5">+24 this month</div>
                    </div>
                    <div className="bg-white rounded-lg p-2.5 shadow-sm border border-gray-100">
                      <div className="text-[10px] text-gray-500 mb-0.5 uppercase tracking-wider">ARPU</div>
                      <div className="text-xs font-bold text-gray-900">$116</div>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-[10px] text-green-600">+8%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props - Outcomes, not features */}
      <section className="pt-24 pb-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-10">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h2 className="font-semibold text-gray-900 mb-2">Instant Stripe Analytics</h2>
              <p className="text-sm text-gray-600">
                Stop exporting spreadsheets. Connect once, see everything.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h2 className="font-semibold text-gray-900 mb-2">Know If Your SaaS Is Healthy</h2>
              <p className="text-sm text-gray-600">
                Get a simple 0-100 health score. Know if you're growing right.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
              </div>
              <h2 className="font-semibold text-gray-900 mb-2">Share Real Traction</h2>
              <p className="text-sm text-gray-600">
                Create a public metrics profile. Build credibility instantly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Not Just Stripe? */}
      <section className="py-16 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              The difference
            </p>
            <h2 className="text-2xl font-bold text-gray-900">
              Stripe shows revenue.{' '}
              <span className="text-primary-600">Plevits shows if your SaaS is healthy.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="mb-4">
                <span className="font-bold text-lg text-[#0043aa]">Stripe Dashboard</span>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Revenue & payments
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Subscriptions
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Customers
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Basic reports
                </li>
                <li className="flex items-center gap-2 pt-2 border-t border-gray-200 mt-2">
                  <span className="text-xs text-gray-400 italic">
                    Great for payments, not for SaaS analytics
                  </span>
                </li>
              </ul>
            </div>
            <div className="bg-primary-50 rounded-xl p-6 border border-primary-100">
              <div className="mb-4">
                <Link to="/" className="inline-flex items-center">
                  <Logo className="h-6 w-auto" />
                </Link>
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">SaaS health score (0-100)</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">Churn & retention metrics</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">Valuation estimate</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">Public traction profile</span>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">Monthly insights & trends</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Made for SaaS founders who:</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
              <svg
                className="w-5 h-5 text-primary-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">Hate building spreadsheets</span>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
              <svg
                className="w-5 h-5 text-primary-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">Want investor-ready metrics</span>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
              <svg
                className="w-5 h-5 text-primary-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">Need to understand growth quickly</span>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
              <svg
                className="w-5 h-5 text-primary-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-700">Run Stripe-based SaaS</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">2 minute setup</h3>
              <p className="text-sm text-gray-500">
                Connect Stripe and see your dashboard instantly
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Read-only access</h3>
              <p className="text-sm text-gray-500">We never touch your money or customer data</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">Built for indie founders</h3>
              <p className="text-sm text-gray-500">
                No enterprise bloat. Just the metrics that matter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">
            From Stripe to insights in 3 steps
          </h2>
          <div className="flex flex-col md:flex-row md:items-start gap-8 md:gap-4">
            <div className="flex-1 text-center">
              <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-semibold">
                1
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Connect Stripe</h3>
              <p className="text-sm text-gray-600">
                One click OAuth. Read-only access. We never touch your money.
              </p>
            </div>
            <div className="hidden md:block flex-shrink-0 mt-5">
              <svg
                className="w-8 h-8 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
            <div className="flex-1 text-center">
              <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-semibold">
                2
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">See Your SaaS Health</h3>
              <p className="text-sm text-gray-600">
                Health score, valuation range, and insights — calculated instantly.
              </p>
            </div>
            <div className="hidden md:block flex-shrink-0 mt-5">
              <svg
                className="w-8 h-8 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
            <div className="flex-1 text-center">
              <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-semibold">
                3
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Share Your Traction</h3>
              <p className="text-sm text-gray-600">
                Create a public profile. Get listed. Build credibility.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Your metrics tell a story. <br />
            Make sure it's the right one.
          </h2>
          <p className="text-primary-100 text-lg mb-8">
            Free to start. 14-day Pro trial included. Your first health score in under 2 minutes.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center px-8 py-3 text-base font-medium text-primary-600 bg-white hover:bg-gray-100 rounded-lg transition-colors"
          >
            Get My SaaS Health Score
          </Link>
          <div className="mt-6 flex items-center justify-center gap-6 text-primary-200 text-sm">
            <Link to="/pricing" className="hover:text-white transition-colors underline">
              See pricing plans
            </Link>
            <Link to="/directory" className="hover:text-white transition-colors underline">
              Browse the SaaS directory
            </Link>
          </div>
        </div>
      </section>

    </PublicLayout>
  );
}
